import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import _ from 'lodash';

import { hasQueryOverrides } from 'components/V5Gadget/seriesDataKey';
import {
  getAlignKey,
  shiftOntoCurrentPeriod,
} from 'hooks/useV5ChartData/queryOverrides/alignGroupResponses';

import getMetrics from '../fetch/getMetrics';
import { filterMetricsForTotals } from '../fetch/filterMetricsForTotals';
import getLimitSetKeywordFilter from '../getLimitSetKeyworkFilter';

// A matrix's columns that ask their own question (series query overrides):
// fetched apart from the table's own query, which picks and sorts the rows,
// then shown as columns of their own. Each such series becomes an alias
// metric, keyed by its id, so the matrix's formatting (keyed by metric id)
// draws it as any other column.
export interface MatrixOverrideGroup {
  key: string;
  series: V5ChartDefinitionSeries[];
  // What the group is fetched with: its series (and the table's sort metric,
  // see toQueryChartDef), filters and dates
  chartDef: V5ChartDefinition;
  filterInput: FilterInput;
  dateScope: DateRangeInput;
  isOffset: boolean;
  usedMetrics: Metrics.Metric[];
  // Keyed by series id: the column header when the series has no name
  seriesNames: Record<string, string>;
}

export interface MatrixOverrides {
  // The chart as drawn, every series in place
  chartDef: V5ChartDefinition;
  groups: MatrixOverrideGroup[];
}

interface FetchedGroup {
  group: MatrixOverrideGroup;
  rows: MetricsResponse;
  totals: MetricsResponse;
}

// A one-dimensional matrix (a column per series, a row per term or date),
// where a series can ask its own question. Other layouts show the first
// series in every cell, a row per series, or several rows per term.
export const canHaveOverrideGroups = (chartDef: V5ChartDefinition): boolean =>
  chartDef.gadgetType === 'matrix' &&
  !chartDef.dimensionB &&
  !chartDef.secondaryDimensionA &&
  !chartDef.reverseAxes;

export const hasOverrideSeries = (chartDef: V5ChartDefinition): boolean =>
  canHaveOverrideGroups(chartDef) &&
  chartDef.series.some((s) => hasQueryOverrides(s.queryOverrides));

// Each group's rows and totals, for the rows the table's own query found:
// its terms, without a limit of their own (dates are all the group's)
export const fetchOverrideGroups = ({
  groups,
  baseRows,
  groupByField,
  currentDateScope,
  toMetricInput,
  client,
  isDashboardGadget,
}: {
  groups: MatrixOverrideGroup[];
  baseRows: MetricsResponse;
  groupByField: string;
  currentDateScope: DateRangeInput;
  toMetricInput: (metric: Metrics.NormalMetric | MetricInput) => MetricInput;
  client: ApolloClient<NormalizedCacheObject>;
  isDashboardGadget?: boolean;
}): Promise<FetchedGroup[]> =>
  Promise.all(
    groups.map(async (group) => {
      const limitedKeywordFilter = getLimitSetKeywordFilter(
        baseRows,
        groupByField,
        group.chartDef.dimensionA,
      );
      const shared = {
        chartDef: group.chartDef,
        filterInput: group.filterInput,
        isDashboardGadget,
        dateScope: group.dateScope,
        toMetricInput,
        client,
        limitedKeywordFilter,
      };
      const totalsMetrics = filterMetricsForTotals({
        usedMetrics: group.usedMetrics,
        chartDef: group.chartDef,
      });
      const [rows, totals] = await Promise.all([
        getMetrics({
          ...shared,
          usedMetrics: group.usedMetrics,
          isIgnoringLimit: true,
        }),
        totalsMetrics.length > 0
          ? getMetrics({
              ...shared,
              usedMetrics: totalsMetrics,
              getTotals: true,
            })
          : Promise.resolve([] as MetricsResponse),
      ]);
      return {
        group,
        rows: group.isOffset
          ? shiftOntoCurrentPeriod({
              response: {
                rows,
                dateScope: group.dateScope,
                isOffset: true,
              },
              currentDateScope,
              chartDef: group.chartDef,
            })
          : rows,
        totals,
      };
    }),
  );

// The groups' values joined onto the table's rows (and totals) under their
// series' ids. Rows only a group has aren't added: the table's own query
// decides the rows.
export const mergeOverrideGroups = ({
  chartDef,
  responses,
  fetched,
}: {
  chartDef: V5ChartDefinition;
  responses: MetricsResponse[];
  fetched: FetchedGroup[];
}): MetricsResponse[] => {
  const alignKey = getAlignKey(chartDef);
  const [rows = [], totals = [], ...rest] = responses;

  const mergedRows = alignKey
    ? rows.map((row) => {
        const key = String(row[alignKey]);
        return fetched.reduce((next, { group, rows: groupRows }) => {
          const match = groupRows.find((r) => String(r[alignKey]) === key);
          return match
            ? { ...next, ...toAliasValues({ group, row: match }) }
            : next;
        }, row);
      })
    : rows;

  const aliasTotals = fetched.reduce(
    (acc, { group, totals: groupTotals }) =>
      groupTotals.length > 0
        ? { ...acc, ...toAliasValues({ group, row: groupTotals[0] }) }
        : acc,
    {} as MetricResponse,
  );
  const mergedTotals = _.isEmpty(aliasTotals)
    ? totals
    : [{ ...(totals[0] ?? {}), ...aliasTotals }, ...totals.slice(1)];

  return [mergedRows, mergedTotals, ...rest];
};

// A group's values for its own series, under their ids
const toAliasValues = ({
  group,
  row,
}: {
  group: MatrixOverrideGroup;
  row: MetricResponse;
}): MetricResponse =>
  group.series.reduce((acc, s) => {
    const value = row[s.metricId];
    return value === undefined ? acc : { ...acc, [s.id]: value };
  }, {} as MetricResponse);

// The chart, metrics and formatter the matrix is drawn with: each overriding
// series a metric of its own (its id), named as it's labelled, formatted as
// its metric, without comparison cells, rank or share of total
export const toAliasedMatrix = ({
  overrides,
  usedMetrics,
  formatMetric,
}: {
  overrides: MatrixOverrides;
  usedMetrics: Metrics.Metric[];
  formatMetric: (args: {
    metricId: string;
    value: string | number | null | undefined;
  }) => string;
}) => {
  const groupSeries = overrides.groups.flatMap((g) =>
    g.series.map((s) => ({ group: g, series: s })),
  );
  const realMetricIds: Record<string, string> = {};
  const aliases = groupSeries.flatMap(({ group, series }) => {
    const metric = group.usedMetrics.find((m) => m.id === series.metricId);
    if (!metric) {
      return [];
    }
    realMetricIds[series.id] = metric.id;
    return [
      {
        ...metric,
        id: series.id,
        name: group.seriesNames[series.id] ?? metric.name,
      } as Metrics.Metric,
    ];
  });
  const chartDef: V5ChartDefinition = {
    ...overrides.chartDef,
    series: overrides.chartDef.series.map((s) =>
      realMetricIds[s.id]
        ? {
            ..._.omit(s, [
              'queryOverrides',
              'matrixCellType',
              'matrixShowComparisonValue',
              'isRankingEnabled',
              'isPercentageOfTotalEnabled',
            ]),
            metricId: s.id,
          }
        : s,
    ),
  };
  return {
    chartDef,
    usedMetrics: [...usedMetrics, ...aliases],
    formatMetric: ({
      metricId,
      value,
    }: {
      metricId: string;
      value: string | number | null | undefined;
    }) =>
      formatMetric({ metricId: realMetricIds[metricId] ?? metricId, value }),
  };
};
